import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES, BRAND } from "@/lib/constants";
import ProductCard from "@/components/ProductCard";
import { MaskedLine, Reveal } from "@/components/Reveal";

const FESTIVE_SLUGS = ["gourmet-confectionery", "signature-collections", "wedding-favors", "tea-coffee"];

const TIMELINE = [
  { date: "By 15 Aug", title: "Brief", text: "Lock your recipient list, budget band and branding directions." },
  { date: "By 15 Sep", title: "Sampling", text: "Approve a physical or photographed sample carrying your logo." },
  { date: "Through Oct", title: "Production", text: "Hand assembly, wrapping and quality checks in our studio." },
  { date: "Early Nov", title: "Delivery", text: "Timed to land before Diwali — to one address or a thousand." },
];

const NOTES = [
  "Saffron, cardamom and single-origin chocolate — festive flavours, no generic mithai.",
  "Your logo in foil, your palette in ribbon and wax, your message inside the lid.",
  "Scales from a 25-box leadership circle to 25,000-box company-wide runs.",
];

export default function Festive() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products")
      .then((r) => setProducts(r.data.filter((p) => FESTIVE_SLUGS.includes(p.category)).slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <main>
      <section className="relative bg-ink-deep text-cream overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_10%,rgba(212,175,55,0.14)_0%,rgba(26,24,23,0)_60%)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-24 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="eyebrow !text-gold/80 flex items-center gap-3"
            >
              <span className="inline-block h-px w-10 bg-gold" />
              The Festive Edit · Diwali 2026
            </motion.p>
            <h1 className="mt-7 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-balance">
              <MaskedLine delay={0.35}>Boxes that outshine</MaskedLine>
              <MaskedLine delay={0.5} className="italic text-gold">the fireworks.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-md text-base text-cream/60 leading-relaxed"
            >
              Corporate Diwali hampers, client gifting and festive favours — curated,
              branded and delivered across India before the diyas are lit. Slots for
              2026 are limited by studio capacity.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <Link
                to="/enquire?occasion=Festive Gifting"
                data-testid="festive-cta-quote"
                className="inline-flex items-center gap-2 bg-gold text-ink px-8 py-4 text-sm font-medium tracking-wide hover:bg-gold-hover transition-colors duration-400 group"
              >
                Book your Diwali brief
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href="#festive-picks" className="text-sm text-cream/80 gold-underline-btn font-medium">
                See this season's picks
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto max-w-[340px] sm:max-w-[400px]"
            >
              <div className="absolute -inset-3 border border-gold/30 rounded-t-[999px]" />
              <div className="overflow-hidden rounded-t-[999px] shadow-[0_40px_80px_rgba(0,0,0,0.45)]">
                <img src={BRAND.board} alt="Gratiora festive packaging" className="w-full aspect-[3/4] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="festive-picks" className="py-20 lg:py-28 px-6 lg:px-12 max-w-7xl mx-auto">
        <Reveal>
          <p className="eyebrow">This season's picks</p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
            Six pieces made for Diwali
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-cream-dark/60 border-y border-gold/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="eyebrow">The festive clock</p>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight text-balance">
              Diwali doesn't wait — here's the run-up
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.1}>
                <div data-testid={`festive-timeline-${i}`} className="relative">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold-brass">{t.date}</span>
                  <div className="mt-3 h-px w-full bg-gradient-to-r from-gold/60 to-transparent" />
                  <h3 className="mt-5 font-serif text-xl sm:text-2xl text-ink">{t.title}</h3>
                  <p className="mt-3 text-sm text-ink-muted leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14 max-w-2xl">
            <ul className="space-y-3">
              {NOTES.map((n) => (
                <li key={n} className="flex items-start gap-3 text-sm text-ink/80">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rotate-45 bg-gold shrink-0" />
                  {n}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-[1.1] text-balance max-w-3xl mx-auto">
            Reserve your production slot <span className="italic text-gold-brass">before the season fills.</span>
          </h2>
          <Link
            to="/enquire?occasion=Festive Gifting"
            data-testid="festive-bottom-cta"
            className="mt-10 inline-flex items-center gap-2 bg-ink text-cream px-10 py-5 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 group"
          >
            Book your Diwali brief
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
